import { useState } from 'react';

import useAsyncRequest from '../../../../../hooks/useAsyncRequest';
import { get } from '../../../../../redux/api';
import { selectOptionFactory } from '../../../../../utils';
import type { Label } from '../../../../../../shared/model-typings';
import type { SelectOption } from '../../../../../types';
import type { SetProjectsFilter } from '../shared/types';

type UseLabelsReturn = {
  isLoading: boolean;
  labels: Label[];
};

const request = () => {
  return get<Record<'labels', Label[]>>('/api/organizations/labels');
};

/**
 * useLabels is a hook that fetches and returns the labels from all projects the
 * current user has access to from /api/organizations/labels.
 */
export const useLabels = (): UseLabelsReturn => {
  const labelsAsyncValue = useAsyncRequest(request);

  const { labels } = labelsAsyncValue.value ?? { labels: [] };

  return {
    isLoading: labelsAsyncValue.isLoading,
    labels,
  };
};

type UseLabelsSelectOptionsReturn = {
  isLoading: boolean;
  labelOptions: SelectOption[];
  selectedLabelOptions: SelectOption[];
  handleLabelsChange: (selected: Array<SelectOption<string>>) => void;
};

/**
 * useLabelsSelectOptions is a hook that invokes useLabels and returns labels formatted as select options.
 * It also exposes state and functions to manage the selected labels.
 *
 * @param setFilters - Function to set the filters in the parent component,
 *  passed down from the shared useReleaseGroupState hook
 */
export const useLabelsSelectOptions = (setFilters: SetProjectsFilter): UseLabelsSelectOptionsReturn => {
  const { labels, isLoading } = useLabels();

  const [selectedLabelOptions, setSelectedLabelOptions] = useState<SelectOption[]>([]);

  const labelOptions = labels.map((label) => selectOptionFactory(label.label));

  const handleLabelsChange = (selected: Array<SelectOption<string>>) => {
    setSelectedLabelOptions(selected);
    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        labels: selected.map((option) => option.value),
      };
    });
  };

  return {
    isLoading,
    labelOptions,
    selectedLabelOptions,
    handleLabelsChange,
  };
};
